import type { SequenceStep } from '~/components/Sequence/sequenceSteps';
import { canMergeIntoGroup, canReceiveMerge } from '~/components/Sequence/sequenceSteps';

/**
 * Cross-surface drag/move engine for the sequence graph. The editor's drag layer resolves a gesture into a
 * `from` step + a `drop`, then calls {@link moveStep} — a single pure function that reorders a step within
 * ANY line (the spine, a spawn arm, a switch branch) OR moves it between lines, OR merges it onto a node to
 * form / join a parallel group. Keeping every case in one tested function is what lets a branch step behave
 * exactly like a spine step (the parallel group's single-step arms are the only special case — those keep
 * the existing extract-drag in sequenceSteps).
 */

/** An ordered, reorderable list of steps you can drag within / drop into. */
export type LineAddr =
  | { kind: 'spine' }
  | { kind: 'spawnArm'; group: number }
  | { kind: 'switchBranch'; group: number; branch: number };

/** Serialize a line address into a `data-line` DOM attribute the drag hit-test can read back. */
export function serializeLine(line: LineAddr): string {
  if (line.kind === 'spine') return 'spine';
  if (line.kind === 'spawnArm') return `spawnArm:${line.group}`;
  return `switchBranch:${line.group}:${line.branch}`;
}

/** Parse a `data-line` attribute back into a {@link LineAddr}, or null if malformed. */
export function parseLine(raw: string | null): LineAddr | null {
  if (raw == null) return null;
  if (raw === 'spine') return { kind: 'spine' };
  const parts = raw.split(':');
  if (parts[0] === 'spawnArm' && parts.length === 2) return { kind: 'spawnArm', group: Number(parts[1]) };
  if (parts[0] === 'switchBranch' && parts.length === 3)
    return { kind: 'switchBranch', group: Number(parts[1]), branch: Number(parts[2]) };
  return null;
}

/** A specific step: the line it lives in + its index there. */
export interface StepAddr {
  line: LineAddr;
  index: number;
}

/** Where a dragged step lands: reordered into a line at a gap (0..len), or merged onto another node. */
export type DropTarget =
  | { kind: 'reorder'; line: LineAddr; gap: number }
  | { kind: 'merge'; onto: StepAddr };

/**
 * A branch/spawn line is addressed by the SPINE index of its container. Removing a spine step before that
 * container shifts it left by one — so after a spine-sourced move we must re-point the target line at the
 * container's new index, or the drop lands nowhere.
 */
function shiftLineAfterSpineRemoval(line: LineAddr, from: StepAddr): LineAddr {
  if (from.line.kind !== 'spine' || line.kind === 'spine') return line;
  return line.group > from.index ? { ...line, group: line.group - 1 } : line;
}

/** Two line addresses point at the same list. */
export function sameLine(a: LineAddr, b: LineAddr): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'spine') return true;
  if (a.kind === 'spawnArm' && b.kind === 'spawnArm') return a.group === b.group;
  if (a.kind === 'switchBranch' && b.kind === 'switchBranch') return a.group === b.group && a.branch === b.branch;
  return false;
}

/** Read the ordered steps of a line (empty array if the address doesn't resolve). */
export function getLine(steps: SequenceStep[], line: LineAddr): SequenceStep[] {
  if (line.kind === 'spine') return steps;
  if (line.kind === 'spawnArm') return steps[line.group]?.arm ?? [];
  return steps[line.group]?.switch?.branches[line.branch]?.arm ?? [];
}

/** The step at an address, or null. */
export function stepAt(steps: SequenceStep[], addr: StepAddr): SequenceStep | null {
  return getLine(steps, addr.line)[addr.index] ?? null;
}

/** Immutably replace a line's step array via `fn`, returning the new spine. */
function updateLine(steps: SequenceStep[], line: LineAddr, fn: (arr: SequenceStep[]) => SequenceStep[]): SequenceStep[] {
  if (line.kind === 'spine') return fn(steps);
  if (line.kind === 'spawnArm') {
    return steps.map((s, i) => (i === line.group && s.type === 'spawn' ? { ...s, arm: fn(s.arm ?? []) } : s));
  }
  return steps.map((s, i) => {
    if (i !== line.group || s.switch == null) return s;
    const branches = s.switch.branches.map((b, bi) => (bi === line.branch ? { ...b, arm: fn(b.arm) } : b));
    return { ...s, switch: { ...s.switch, branches } };
  });
}

/** Turn the node at `idx` into (or grow) a parallel group by adding `step` as a new arm (target first). */
function mergeInto(arr: SequenceStep[], idx: number, step: SequenceStep): SequenceStep[] {
  const target = arr[idx];
  if (target == null) return arr;
  const next = arr.slice();
  if (target.type === 'group') next[idx] = { ...target, arms: [...(target.arms ?? []), [step]], join: 'all' };
  else next[idx] = { type: 'group', join: 'all', arms: [[target], [step]] };
  return next;
}

/**
 * Whether a `step` may LIVE in `line` — the same container rule the "+" picker enforces (mirrors the
 * server's `normalizeStep` container modes). The spine (top level) holds anything; a branch / spawn arm
 * holds plain steps + group / spawn, but NEVER a `switch` (a switch's fan only nests at the top — dropping
 * one into a branch would create the "hidden paths inside a branch" that must be impossible).
 */
export function canMoveToLine(step: SequenceStep, line: LineAddr): boolean {
  if (line.kind === 'spine') return true;
  return step.type !== 'switch';
}

/**
 * Whether `from` can be dropped as `drop` — the dragged step must be a basic (groupable) step to merge, a
 * reorder that doesn't move it is a no-op, and (crucially) the step must be ALLOWED in the target line
 * ({@link canMoveToLine}) so drag can never build a tree the picker would reject. Mirrors the spine's guards
 * so every surface accepts exactly the same gestures.
 */
export function canDrop(steps: SequenceStep[], from: StepAddr, drop: DropTarget): boolean {
  const step = stepAt(steps, from);
  if (step == null) return false;
  if (drop.kind === 'merge') {
    if (sameLine(drop.onto.line, from.line) && drop.onto.index === from.index) return false; // onto itself
    // A merge wraps the two steps into a group AT the target line — so the group must be allowed there, and
    // the dragged step must be groupable and the target able to receive it.
    return canMoveToLine({ type: 'group' }, drop.onto.line) && canMergeIntoGroup(step) && canReceiveMerge(stepAt(steps, drop.onto));
  }
  // The step must be allowed to live in the target line (no switch inside a branch, etc.).
  if (!canMoveToLine(step, drop.line)) return false;
  // A switch ENDS the main line — it must stay the last spine step, and nothing may land after it (that
  // step could never run). So a spine reorder can't drop a step past the switch, nor move the switch itself
  // off the end.
  if (drop.line.kind === 'spine') {
    const switchIdx = steps.findIndex((s) => s.type === 'switch');
    if (switchIdx >= 0) {
      const removedBefore = from.line.kind === 'spine' && from.index < switchIdx ? 1 : 0;
      const switchAfter = switchIdx - removedBefore;
      const insertAt = from.line.kind === 'spine' && drop.gap > from.index ? drop.gap - 1 : drop.gap;
      if (step.type === 'switch') {
        if (insertAt < steps.length - 1) return false; // the switch itself must stay last
      } else if (insertAt > switchAfter) {
        return false; // a normal step can't land after the switch
      }
    }
  }
  // A reorder to the gap just before or after the step's own slot (same line) leaves the order unchanged.
  if (sameLine(drop.line, from.line) && (drop.gap === from.index || drop.gap === from.index + 1)) return false;
  return true;
}

/**
 * Apply a drag: move `from` to `drop`. Reorders within a line, moves between lines (spine ↔ spawn arm ↔
 * switch branch), or merges onto a node to form/join a group. Returns the original array unchanged if the
 * move isn't allowed ({@link canDrop}). Pure.
 */
export function moveStep(steps: SequenceStep[], from: StepAddr, drop: DropTarget): SequenceStep[] {
  if (!canDrop(steps, from, drop)) return steps;
  const step = stepAt(steps, from);
  if (step == null) return steps;

  // Remove the step from its source line first; every downstream index is then computed on this array.
  const removed = updateLine(steps, from.line, (arr) => arr.filter((_, i) => i !== from.index));

  if (drop.kind === 'merge') {
    // Same-line: the target's index shifts left by one if it sat AFTER the removed step. Cross-line off the
    // spine: the target line's container index may have shifted left too.
    const ontoIndex =
      sameLine(drop.onto.line, from.line) && drop.onto.index > from.index ? drop.onto.index - 1 : drop.onto.index;
    const ontoLine = shiftLineAfterSpineRemoval(drop.onto.line, from);
    return updateLine(removed, ontoLine, (arr) => mergeInto(arr, ontoIndex, step));
  }

  // Reorder: a gap past the removed step in the SAME line shifts left by one; a cross-line target off the
  // spine may need its container index shifted too.
  const gap = sameLine(drop.line, from.line) && drop.gap > from.index ? drop.gap - 1 : drop.gap;
  const line = shiftLineAfterSpineRemoval(drop.line, from);
  return updateLine(removed, line, (arr) => {
    const next = arr.slice();
    next.splice(Math.max(0, Math.min(gap, next.length)), 0, step);
    return next;
  });
}
